import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { tscope } from '@i18n';
import { fileValidator } from '@components/FileUploader/fileValidator';

const errorT = tscope('FormResolver');

const validateFile = async ([name, value], values) => {
  if (!value?.files?.length) return null;
  const file = value.files[0];
  const error = await fileValidator({ file, password: values[`${name}-password`] });
  return error;
};

const validateFilesAsync = async (values) => {
  const errors = {};
  await Promise.all(
    Object
      .entries(values)
      .map(([name, value]) => {
        if (name.endsWith('-password')) return null;
        const appendErrorAsync = async () => {
          const error = await validateFile([name, value], values);
          if (error && error?.errorCode) errors[`${name}-password`] = error;
          else if (error) errors[name] = error;
        };
        return appendErrorAsync();
      }),
  );

  return errors;
};

const resolver = async (values) => {
  const errors = {};
  const errorsInFiles = await validateFilesAsync(values);
  Object.assign(errors, errorsInFiles);

  return {
    values,
    errors,
  };
};

export const useDocumentForm = () => {
  const {
    reset,
    control,
    setError,
    resetField,
  } = useForm({
    mode: 'onChange',
    resolver,
  });

  const onUploadError = useCallback(({ fieldName }) => {
    setError(fieldName, { type: 'danger', message: errorT('uploadError') });
  }, [setError]);

  const onUploadCompleted = useCallback(({ fieldName }) => {
    resetField(fieldName, { defaultValue: undefined });
    resetField(`${fieldName}-password`);
  }, [resetField]);

  const clearField = useCallback(({ fieldName }) => {
    resetField(fieldName, { defaultValue: undefined });
    resetField(`${fieldName}-password`);
  }, [resetField]);

  const resetForm = useCallback(() => {
    reset();
  }, [reset]);

  return {
    control,
    resetForm,
    clearField,
    onUploadError,
    onUploadCompleted,
  };
};
